import styled from "@emotion/styled"
import * as uniform from "@uniformdev/context-react"
import { Icon } from "components/icon/Icon"
import Bento from "components/nav-v2/bento/Bento"
import BentoController from "components/nav-v2/bento/Controller"
import { cssVars, useNav } from "components/nav-v2/Wrapper"
import Cta from "components/sub-nav/Cta"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useNavigation } from "contexts/Navigation"
import { usePage } from "contexts/Page"
import { contrastMode } from "css/a11y"
import getMediaQuery, { breakpoint } from "css/breakpoints"
import { buttonReset } from "css/buttons"
import { body50, bold, legend90, normal } from "css/text"
import * as focusTrap from "focus-trap"
import { animate, scroll, useMotionValueEvent } from "framer-motion"
import { useLayoutEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

const Wrapper = styled.nav`
  position: fixed;
  z-index: 9998;
  inset: 0;
  inset-block-start: 5px;
  inset-block-end: 5px; /* keep IOS chrome transparent <!>*/
  pointer-events: none;
  overflow: hidden;

  [data-cfg="true"] & {
    z-index: -1;
  }

  &:focus-within,
  &[data-has-focus="true"] {
    ${cssVars.sub.toggle.pinned}: 1 !important;
    ${cssVars.sub.toggle.forceUnpinned}: 0 !important;
  }
`
const Pane = styled.div`
  --installed: 0;

  &[data-installed="false"] {
    pointer-events: none;

    transform: scale(var(--installed), var(--installed));
    transform-origin: center;

    & .toggle {
      overflow: hidden;
      --t: clamp(0, (var(--installed) - 0.8) * 5, 1);

      & > * {
        opacity: var(--t);
      }

      & > *:nth-child(1) {
        transform: translate3d(calc((1 - var(--t)) * -5rem), 0, 0);
      }

      & > *:nth-child(2) {
        transform: translate3d(calc((1 - var(--t)) * 5rem), 0, 0);
      }
    }
  }

  --content-display: 0;
  --inset-inline: 1.8rem;
  --heading-height: 3rem;
  --heading-margin-block-end: 1.25rem;
  --nav-container-height: 7rem;

  @media (min-width: ${breakpoint.m}) {
    & {
      --nav-container-height: 4rem;
    }
  }

  &[data-actions*="lock"] {
    ${cssVars.sub.toggle.pinned}: var(${cssVars.sub.lock}) !important;
    ${cssVars.sub.toggle.forceUnpinned}: 0 !important;
  }

  --nav-sub-collapsed-width: var(--nav-sub-expanded-width);
  --nav-sub-collapsed-height: 3.375rem;
  --nav-sub-expanded-width: min(90vw, max(55vw, 512px), 512px);

  --nav-sub-expanded-height: min(80dvh, var(--height, var(--nav-sub-expanded-width)));

  --nav-sub-collapsed-inset-inline-start: calc((100vw - var(--nav-sub-collapsed-width)) * 0.5);
  --nav-sub-collapsed-inset-block-end: calc(
    3.5vh - (3.5vh + var(--nav-sub-collapsed-height)) *
      max(1 - max(var(${cssVars.sub.toggle.pinned}), var(${cssVars.sub.pane.expanded})), var(${cssVars.sub.toggle.forceUnpinned}, 0))
  );
  --nav-sub-expanded-inset-inline-start: calc((100vw - var(--nav-sub-expanded-width)) * 0.5);
  --nav-sub-expanded-inset-block-end: calc((100dvh - var(--nav-sub-expanded-height)) * 0.5);

  @media (min-width: ${breakpoint.xxll}) {
    &[data-expandable="true"] {
      /* todo */
    }
  }

  width: calc(var(--nav-sub-collapsed-width) + (var(--nav-sub-expanded-width) - var(--nav-sub-collapsed-width)) * var(${cssVars.sub.pane.expanded}, 0));
  height: calc(var(--nav-sub-collapsed-height) + (var(--nav-sub-expanded-height) - var(--nav-sub-collapsed-height)) * var(${cssVars.sub.pane.expanded}, 0));

  inset-inline-start: calc(
    var(--nav-sub-collapsed-inset-inline-start) + (var(--nav-sub-expanded-inset-inline-start) - var(--nav-sub-collapsed-inset-inline-start)) *
      var(${cssVars.sub.pane.expanded}, 0)
  );
  inset-block-end: calc(
    var(--nav-sub-collapsed-inset-block-end) + (var(--nav-sub-expanded-inset-block-end) - var(--nav-sub-collapsed-inset-block-end)) *
      var(${cssVars.sub.pane.expanded}, 0)
  );

  position: absolute;
  border-radius: 0.38rem;
  opacity: clamp(0, max(var(${cssVars.sub.toggle.pinned}, 1), var(${cssVars.sub.toggle.forceUnpinned}, 0)) * 2, 1);
  background: rgb(from white r g b / calc(0.7 + 0.3 * var(${cssVars.sub.pane.expanded}, 0)));
  backdrop-filter: blur(5px);
  pointer-events: none;
  transition: background 300ms;

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      background: rgb(from white r g b / calc(0.9 + 0.3 * var(${cssVars.sub.pane.expanded}, 0)));
    }
  }
`

const Content = styled.div`
  overflow-x: hidden;
  overflow-y: hidden;

  &[data-scrollable="1"] {
    overflow-y: auto;
  }

  position: absolute;
  inset: 0;

  // display: grid;
  // grid-template-rows: [heading] minmax(0, max-content) [bento] minmax(0, 1fr) [navigation] minmax(0, max-content);
  content-visibility: hidden;
  // opacity: var(${cssVars.sub.pane.expanded}, 0);

  & > .wrap {
    height: min-content !important;
    padding-inline: var(--inset-inline);
    padding-block: var(--inset-inline);
    opacity: var(--content-display);
  }
`

const Heading = styled.div`
  font-size: 20px;
  ${bold}
  color: rgb(var(--green));
  line-height: 1.2rem;
  display: flex;
  position: relative;
  margin-block-end: 1.25rem;
  align-items: center;
  justify-content: center;
  max-width: 75%;
  margin-inline: auto;
  text-align: center;

  ${getMediaQuery("m")} {
    max-width: unset;
    margin-inline: unset;
    text-align: unset;
    font-size: 24px;
    line-height: 1rem;
  }

  [data-variant="retailer"] & {
    display: none;
  }
`

const NavContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-start: 0.9rem;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  ${getMediaQuery("l")} {
    max-width: 22.5rem;
    margin-inline: auto;
    align-items: center;
  }

  > button {
    --sub-nav-back-height: 2.5rem;
    height: var(--sub-nav-back-height);
    /* line-height: 1.375rem; */
    font-size: 14px !important;

    ${getMediaQuery("m")} {
      /* line-height: 1.625rem; */
      --sub-nav-back-height: 2.75rem;
      font-size: 16px !important;
    }
  }
`

const Toggle = styled.button`
  display: grid;
  place-items: center;
  width: 100%;
  height: 3.375rem;
  opacity: calc(1 - var(${cssVars.sub.pane.expanded}, 0));
  background: transparent;
  border: none;
  position: relative;
  color: black;
  border-radius: 0.38rem;
  box-shadow: 0 4px 28px 0 rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  justify-content: space-between;
  // border: 0.5px solid rgba(var(--pure-white) / 0.5);
  transition: box-shadow 300ms;
  overflow: hidden;
  line-height: 1.375rem;

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
  .textBlock {
    display: flex;
    flex-direction: row;
    overflow: hidden;
    min-width: 0;
    width: 100%;

    > div {
      display: flex;
      width: auto;
      flex-shrink: 0;

      display: flex;
      width: auto;
      > span:not(.hidden) {
        margin-inline-start: 1.25rem;
        color: rgb(var(--green));
        ${normal}
        ${contrastMode}
      }
      .hidden {
        opacity: 0;
        height: 0.1px;
        width: 0.1px;
      }
    }

    > span:not(.page-count) {
      color: rgb(var(--green));
      ${bold}
      ${contrastMode}

    @media (max-width: ${breakpoint.m}) {
        width: 50%;
        min-width: 0;
        flex: 1 1 auto;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        padding-inline-end: 2.75rem;
      }
    }
  }

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      box-shadow: 0 4px 28px 0 rgba(0, 0, 0, 0.15);

      > div > svg {
        fill: rgb(var(--green));
      }
    }
  }
`
const ToggleIcon = styled.div`
  position: absolute;
  inset-inline-end: 1.88rem;
  pointer-events: none;
  display: flex;
  align-items: center;

  & svg {
    fill: rgb(var(--light-black));
    width: 1.2rem;
    height: 1.2rem;
  }
`

const Progress = styled.div`
  position: absolute;
  inset-inline: 0;
  inset-block-end: 0;
  height: 0.25rem;
  --progress: 0;

  & > div {
    width: calc(100% * var(--progress, 0));
    height: 100%;
    background: rgb(var(--green));
    border-radius: 0 9999px 9999px 0;
  }
`

const Back = styled.div`
  display: flex;
  position: absolute;
  display: grid;
  place-items: center;
  --sub-nav-back-height: 2.25rem;
  bottom: calc((1.88rem + var(--sub-nav-back-height)) * -1);
  width: 100%;
  height: var(--sub-nav-back-height);
  opacity: calc(clamp(0, (var(--content-display) - 0.3) * 1.5, 1));
  pointer-events: auto;
  border-radius: 50px;

  @media (max-height: 667px) {
    bottom: max(var(--sub-nav-back-height) * -1 - 5px, (1.88rem + var(--sub-nav-back-height)) * -1);
  }

  > a {
    --sub-nav-back-height: 2.25rem;
    display: flex;
    border-radius: 50px;
    padding-inline: 1.88rem;
    background-color: rgb(var(--pure-white));
    /* min-width: clamp(80px, 25vw, 150px); */
    align-items: center;
    justify-content: center;
    text-decoration: none;
    color: currentColor;
    height: var(--sub-nav-back-height);
    transition: color 300ms;

    ${getMediaQuery("m")} {
      --sub-nav-back-height: 2.75rem;
      padding-inline: 2.75rem;
    }

    @media (any-hover: hover) and (any-pointer: fine) {
      &:hover {
        color: rgb(var(--green));
      }
    }

    > span {
      ${bold}
      font-size: 12px;
      ${getMediaQuery("m")} {
        font-size: 14px !important;
      }
    }
  }
`

const Close = styled.button`
  ${buttonReset}
  display: flex;
  color: rgb(var(--light-black));
  position: fixed;
  inset-inline-end: var(--inset-inline, 0);
  inset-block-start: 0;
  transform: translateY(1.25rem);
  border: 0.5px solid rgb(var(--grey));
  border-radius: 9999px;
  padding: 0.575rem;
  z-index: 3;
  background-color: rgb(var(--pure-white));

  > svg {
    transition: fill 300ms;
  }

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      > svg {
        fill: rgb(var(--green));
      }
    }
  }

  opacity: calc(clamp(0, (var(--content-display) - 0.3) * 1.5, 1));
`

export const Action = styled.a`
  font-size: 14px;
  ${bold}
  --sub-nav-back-height: 2.375rem;
  height: calc(var(--sub-nav-back-height));
  font-size: 14px !important;
  display: flex;
  width: calc(100% - 2rem);
  text-decoration: none;
  color: currentColor;
  border: 1px solid rgb(var(--grey));
  border-radius: 50px;
  padding-inline: 1rem;
  justify-content: center;
  transition: color 300ms;
  align-items: center;
  column-gap: 0.5rem;
  text-align: center;
  [dir="rtl"] & {
    // flex-direction: row-reverse;
    // direction: rtl;
  }

  ${getMediaQuery("m")} {
    --sub-nav-back-height: 2.75rem;
    height: calc(var(--sub-nav-back-height) - 0.125rem);

    /* line-height: 1rem; */
    font-size: 16px !important;
  }

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      color: rgb(var(--green));
    }
  }
`

const RetailerList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 1.5rem 0 4.44rem;
  row-gap: 1.5rem;
  text-align: center;
`
const RetailerListItem = styled.li`
  ${bold}
  list-style: none;
  font-size: 1rem;
  line-height: 1.375;

  a {
    color: var(--light-black);
    text-decoration: none;
    transition: color 300ms;
  }

  &.active a {
    color: rgb(var(--green));
    cursor: default;
  }

  @media (hover: hover) {
    &:hover a {
      color: rgb(var(--green));
    }
  }
`

const NavSub = ({ children, ...props }) => {
  const console = useConsole()
  const nav = useNav()
  const rwrapper = useRef(null)
  const rpane = useRef(null)
  const rtoggle = useRef(null)
  const rback = useRef(null)
  const rcontent = useRef(null)
  const rclose = useRef(null)
  const navigation = useNavigation()
  const location = useLocation()
  const rtitle = useRef(null)
  const rprogress = useRef(null)
  const scores = uniform.useScores()
  const { subnav_heading } = props
  const dictionary = useDictionary()
  const page = usePage()
  const { page_count, title_page_count } = page
  const currentPage = page_count?.split("/")[0]
  const totalPages = page_count?.split("/")[1]

  useMotionValueEvent(nav.sub.toggle.motion.installed, "change", latest => {
    rpane.current.style.setProperty("--installed", latest)
    if (latest === 1) rpane.current.dataset.installed = !!latest
  })

  useMotionValueEvent(nav.sub.pane.content.motion.display, "change", latest => {
    rpane.current.style.setProperty("--content-display", latest)
  })

  useMotionValueEvent(nav.sub.toggle.motion.pinned, "change", latest => {
    const previous = nav.sub.toggle.motion.pinned.getPrevious()

    if (!previous || !latest) {
      rtoggle.current.style.setProperty("pointer-events", latest > 0 ? "auto" : "none")
    }

    if (previous < 1 && latest === 1) {
      animate(nav.sub.toggle.motion.installed, 1, { duration: 0.45, ease: "easeOut" })
    }
  })

  useMotionValueEvent(nav.sub.pane.motion.expanded, "change", latest => {
    const previous = nav.sub.pane.motion.expanded.getPrevious()

    if (!previous || !latest) {
      rpane.current.style.setProperty("pointer-events", latest > 0 ? "auto" : "none")
      rtoggle.current.style.setProperty("pointer-events", latest > 0 ? "none" : "auto")
      rcontent.current.style.setProperty("content-visibility", latest > 0 ? "visible" : "hidden")
    }
  })

  /* toggle OR content become inert when pane is expanded OR not */
  const onpaneexpandedchange = latest => {
    const previous = nav.sub.pane.motion.expanded.getPrevious()
    const content = rcontent.current
    const back = rback.current
    const toggle = rtoggle.current

    if (!previous || !latest) {
      content.inert = latest === 0
      back.inert = latest === 0
      toggle.inert = latest > 0
    }

    if (latest === 1 || previous === 1) {
      content.dataset.scrollable = latest === 1 ? 1 : 0
    }
  }
  useMotionValueEvent(nav.sub.pane.motion.expanded, "change", onpaneexpandedchange)
  useLayoutEffect(() => {
    onpaneexpandedchange(nav.sub.pane.motion.expanded.get())
  })

  useLayoutEffect(function focusTrapMgmt() {
    const controller = new AbortController()
    let trapTimeout
    let trap = null

    const onpaneexpandedchange = latest => {
      if (trap && latest === 0) {
        trap.deactivate()
        trap = null
        document.activeElement.blur()
        rtoggle.current.focus()

        if (document.activeElement === rtoggle.current && !Object.hasOwn(rtoggle.current.dataset, "focusVisibleAdded")) {
          document.activeElement.blur()
        }
      } else if (!trap && latest > 0) {
        const initialFocus = rclose.current
        const stack = [rcontent.current, rback.current]
        trap = focusTrap.createFocusTrap(stack, {
          allowOutsideClick: true,
          initialFocus,
          isolateSubtrees: "aria-hidden",
          returnFocusOnDeactivate: false,
          onActivate: () => {
            rwrapper.current.dataset.hasFocus = "true" // make sure toggle is marked as pinned
          },
          onDeactivate: () => {
            delete rwrapper.current.dataset.hasFocus // make sure toggle is set back to normal behavior
          },
        })

        clearTimeout(trapTimeout)
        trapTimeout = setTimeout(() => {
          trap.activate()
        }, 4)
      }
    }
    const off = nav.sub.pane.motion.expanded.onChange(onpaneexpandedchange)
    onpaneexpandedchange(nav.sub.pane.motion.expanded.get())

    return () => {
      clearTimeout(trapTimeout)
      if (trap) trap.deactivate()
      controller.abort()
      off()
    }
  })

  useLayoutEffect(function measureContentHeight() {
    const controller = new AbortController()
    const onresize = () => {
      const height = rcontent.current.querySelector(".wrap").scrollHeight
      rwrapper.current.style.setProperty("--height", `${height}px`)
    }

    global.addEventListener("resize", onresize, { signal: controller.signal })
    const off = nav.sub.pane.motion.expanded.on("change", onresize)
    onresize()
    return () => {
      controller.abort()
      off()
    }
  })

  useLayoutEffect(() => {
    const controller = new AbortController()
    const pane = rpane.current

    nav.addEventListener(
      "sub:lock",
      () => {
        pane.dataset.actions = "lock"
      },
      { signal: controller.signal }
    )

    nav.addEventListener(
      "sub:unlock",
      () => {
        pane.dataset.actions = ""
      },
      { signal: controller.signal }
    )

    return () => controller.abort()
  })

  useLayoutEffect(() => {
    const progress = rprogress.current
    const main = document.querySelector("main")

    if (!progress) return

    const cancel = scroll(
      p => {
        progress.style.setProperty("--progress", p)
      },
      { target: main, axis: "y", offset: ["start start", "end end"] }
    )

    return () => {
      cancel()
    }
  })

  const expand = () => {
    animate(nav.sub.pane.motion.expanded, 1)
    animate(nav.sub.pane.content.motion.display, 1, { duration: 0.4, ease: "easeOut", delay: 0.2 })
  }

  const collapse = () => {
    nav.collapseAll()
  }

  const navigate = e => {
    e.preventDefault()
    navigation.navigate(e.currentTarget.href)
  }
  const isLinkActive = item => item.href === navigation.slug
  const close = e => {
    e.preventDefault()
    nav.collapseAll()
  }

  const chooseBento = (bentos, bentos_p13n) => {
    let international = bentos.map(bento => bento.reference?.at?.(0))?.at?.(0)
    const variants = bentos_p13n
      .map(bento => {
        const component = bento.reference?.at?.(0)
        const criteria = bento.criteria?.name?.crit

        if (component && criteria) return { criteria, component }
        if (!criteria && component && !international) {
          international = component
        }
        return null
      })
      .filter(Boolean)

    for (const variant of variants) {
      const hit = variant.criteria.every(crit => {
        const { l: signal, op: operator, r: value } = crit

        switch (operator) {
          case "=":
            return +scores[signal] === +value
          default:
            return false
        }
      })

      if (hit) {
        return variant.component
      }
    }

    return international || null
  }

  const matchesCriteria = criteria => {
    if (!Array.isArray(criteria)) return false

    return criteria.every(crit => {
      const { l: signal, op: operator, r: value } = crit

      switch (operator) {
        case "=":
          return +scores[signal] === +value
        default:
          return false
      }
    })
  }

  const chooseActions = items => {
    const actions = items.map(item => item.action).filter(Boolean)
    const defaultActionsP13n = []
    const matchingActionsP13n = []

    for (const item of items) {
      const action_p13n = item.action_p13n
      if (!action_p13n) continue

      const criteria = action_p13n.criteria?.name?.crit
      if (!Array.isArray(criteria)) {
        defaultActionsP13n.push(action_p13n)
        continue
      }

      if (matchesCriteria(criteria)) matchingActionsP13n.push(action_p13n)
    }

    if (matchingActionsP13n.length) return matchingActionsP13n
    if (actions.length) return actions
    return defaultActionsP13n
  }

  const bentos = (props.module ?? []).map(item => item.bento).filter(Boolean)
  const bentos_p13n = (props.module ?? []).map(item => item.bento_p13n).filter(Boolean)
  const actions = chooseActions(props.module ?? [])
  const actions_retailer = (props.module ?? []).map(item => item.action_retailer).filter(Boolean)
  const cards = (props.module ?? []).map(item => item.card_retailer).filter(Boolean)
  const config_launcher = (props.module ?? []).map(item => item.config_launcher).filter(Boolean)[0]
  const globe_launcher = (props.module ?? []).map(item => item.globe_launcher).filter(Boolean)[0]
  const bento = cards.length ? null : chooseBento(bentos, bentos_p13n)
  const blocks = [bento && "bento", actions.length && "actions", cards.length && `cards-${cards.length}`].filter(Boolean).join(",")

  console.verbose("Nav:Sub(%o)", { props, actions, bento, cards, config_launcher, globe_launcher, actions_retailer })
  return (
    <Wrapper ref={rwrapper} aria-modal='true' role='dialog' aria-label={dictionary.subnavigation()}>
      <Pane
        ref={rpane}
        data-blocks={blocks}
        data-expandable={false /*bento.grids.length > 1*/}
        className='sub-pane'
        data-variant={props.variant}
        data-installed='false'
      >
        <Content ref={rcontent}>
          <Close aria-label={dictionary.close()} ref={rclose} onClick={collapse}>
            <Icon type='close' size='10' />
          </Close>
          <div className='wrap'>
            <BentoController defaultTitle={subnav_heading} motion={nav.sub.pane.motion.expanded} rtitle={rtitle} verticalIfDesktop={false}>
              {children}
              <Heading>
                <p ref={rtitle}>{subnav_heading}</p>
              </Heading>

              {bento && <Bento {...bento} key={bento.uid} />}
              {cards.length ? (
                <RetailerList>
                  {cards.map(card => {
                    const url = navigation.localize(card.link)
                    const isActive = location.pathname === url
                    return (
                      <RetailerListItem key={card.link} className={isActive ? "active" : ""}>
                        <a href={url} aria-label={card.heading.title} onClick={navigate}>
                          <span>{card.heading.title}</span>
                        </a>
                      </RetailerListItem>
                    )
                  })}
                </RetailerList>
              ) : null}
              <NavContainer>
                {actions_retailer.length > 0
                  ? actions_retailer.map(item => (
                      <Action {...item} onClick={() => item.onClick(item)} key={item.label}>
                        <Icon type={item.type} />
                        {item.label}
                      </Action>
                    ))
                  : (props.module ?? []).map((item, i) => {
                      if (item.config_launcher) return <Cta {...item.config_launcher} key={`config-${i}`} />
                      if (item.globe_launcher) return <Cta {...item.globe_launcher} key={`globe-${i}`} />
                      const chosenAction = [item.action, item.action_p13n].find(a => a && actions.includes(a))
                      if (chosenAction) {
                        const { criteria, ...actionItem } = chosenAction
                        return (
                          <Action
                            {...actionItem}
                            href={navigation.localize(actionItem.href)}
                            key={actionItem.label}
                            onClick={e => (isLinkActive(actionItem) ? close(e) : navigate(e))}
                          >
                            {actionItem.label}
                          </Action>
                        )
                      }
                      return null
                    })}
              </NavContainer>
            </BentoController>
          </div>
        </Content>
        <Back ref={rback}>
          {props.back.url !== "" && (
            <a href={navigation.localize(props.back.url)} aria-label={props.back.aria_label} onClick={navigate}>
              <span>{props.back.label}</span>
            </a>
          )}
        </Back>
        <Toggle ref={rtoggle} type='button' onClick={expand} className='toggle'>
          <div className='textBlock'>
            {page_count && (
              <div>
                <span aria-hidden='true' className='page-count'>
                  {page_count}
                </span>
                <span className='hidden'>{dictionary.pageCountA11y(currentPage, totalPages)}</span>
              </div>
            )}
            {title_page_count ? (
              <span style={{ marginInlineStart: page_count ? "0.5rem" : "1.25rem" }}>{title_page_count}</span>
            ) : (
              <span style={{ marginInlineStart: page_count ? "0.5rem" : "1.25rem" }}>{subnav_heading}</span>
            )}
          </div>
          <ToggleIcon>
            <Icon type='chevron' style={{ transform: "rotate(-90deg)" }} />
          </ToggleIcon>
          {page_count && (
            <Progress ref={rprogress}>
              <div />
            </Progress>
          )}
        </Toggle>
      </Pane>
    </Wrapper>
  )
}

export default NavSub
